import React, { PropsWithChildren } from "react";
import {
  Box,
  Button,
  Container,
  Modal,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { BG_COLORS, env } from "../../constants";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectAmazonAccount } from "../../features/user/user.selector";
import { ReactComponent as LockIcon } from "../../assets/amazonAccount/lock.svg";
import { ReactComponent as RevokeIcon } from "../../assets/amazonAccount/revoke.svg";
import { ReactComponent as TickIcon } from "../../assets/amazonAccount/blue-tick.svg";
import { setAmazonAccount } from "../../features/user/user.slice";
// import { selectIsFreePlan } from "../../features/pricingPlans/pricingPlans.selector";

const BenefitItem: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <SvgIcon component={TickIcon} />
      <Typography fontSize="16px" color="#13191F" fontWeight="600">
        {children}
      </Typography>
    </Stack>
  );
};

export const SellerCentralModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isValid, isConnected, shouldShowModal } =
    useAppSelector(selectAmazonAccount);
  // const isFreePlan = useAppSelector(selectIsFreePlan);
  const theme = useTheme();
  const zIndex = Math.max(...Object.values(theme.zIndex)) + 1;
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  const handleConnect = () => {
    window.open(
      `https://sellercentral.amazon.com/apps/authorize/consent?application_id=${env.AMAZON_APP_ID}&state=na&version=beta`,
      "_blank",
    );
    dispatch(setAmazonAccount({ shouldShowModal: false }));
  };

  const handleClose = () => {
    dispatch(setAmazonAccount({ shouldShowModal: false }));
  };

  return (
    <Modal
      // open={!isValid && shouldShowModal && isFreePlan}
      open={!isValid && shouldShowModal}
      sx={{ zIndex }}
      tabIndex={-1}
      disableAutoFocus
    >
      <Stack
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "700px",
          maxWidth: "100%",
          bgcolor: BG_COLORS.default,
          border: "none",
          p: 6,
          borderRadius: 1,
        }}
      >
        <Typography
          textAlign="center"
          fontSize="24px"
          fontWeight={600}
          color="#13191F"
        >
          Connect your Amazon Seller Account
        </Typography>
        <Typography
          textAlign="center"
          fontSize="14px"
          color="#485E75"
          fontWeight={400}
        >
          (optional but recommended)
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: smUp ? "row" : "column",
            alignItems: "center",
            border: "1px solid #D9E0E8",
            borderRadius: "6px",
            padding: "12px",
            marginTop: 5,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            marginRight={smUp ? "12px" : 0}
            marginBottom={smUp ? 0 : "12px"}
            spacing={1}
          >
            <SvgIcon component={LockIcon} />
            <Typography
              textAlign="left"
              fontSize="14px"
              color="#485E75"
              fontWeight={400}
            >
              Protect your business security and data privacy
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            borderLeft={smUp ? "1px solid #D9E0E8" : "none"}
            borderTop={smUp ? "none" : "1px solid #D9E0E8"}
            paddingLeft={smUp ? "12px" : 0}
            paddingTop={smUp ? 0 : "12px"}
            spacing={1}
          >
            <SvgIcon component={RevokeIcon} />
            <Typography
              textAlign="left"
              fontSize="14px"
              color="#485E75"
              fontWeight={400}
            >
              Revoke access to Seller Central at any time
            </Typography>
          </Stack>
        </Box>

        <Typography
          textAlign="left"
          fontSize="14px"
          color="#485E75"
          fontWeight={400}
          marginTop={5}
        >
          Once connected, you'll be able to:
        </Typography>

        <Stack spacing={2} marginTop={2}>
          <BenefitItem>
            Unlock real-time pricing, sales, and competition insights.
          </BenefitItem>
          <BenefitItem>
            Automatically calculate profits with accurate FBA fees.
          </BenefitItem>
          <BenefitItem>
            Save hours of manual research with automated product insights.
          </BenefitItem>
        </Stack>

        <Button
          variant="contained"
          size="large"
          sx={{ mt: 4 }}
          onClick={handleConnect}
        >
          Connect to Seller Central
        </Button>
        <Button
          variant="outlined"
          size="large"
          sx={{ mt: 1 }}
          onClick={handleClose}
        >
          Remind me later
        </Button>
      </Stack>
    </Modal>
  );
};
